import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

interface OrgUnit { orgUnitId:number; name:string; parentOrgUnitId?:number|null }
interface Member { upn:string; displayName:string; role:string }
interface DirectoryEmployee { EmployeeId:number; Upn:string; DisplayName:string|null; ManagerUpn?:string|null }

const AdminPage:React.FC = () => {
  const api = useApi();
  const qc = useQueryClient();
  const { isAdmin, token } = useAuth();
  const { showToast } = useToast();
  const [newName, setNewName] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<number|undefined>();
  const [addUpn, setAddUpn] = useState('');
  const [addRole, setAddRole] = useState<'member'|'manager'>('member');
  const [rename, setRename] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');

  const unitsQ = useQuery({
    queryKey:['org-units', !!token],
    queryFn: () => api.get('/org/units') as Promise<OrgUnit[]>,
    enabled: !!token
  });

  const membersQ = useQuery({
    queryKey:['org-members', selectedUnit, !!token],
    enabled: !!selectedUnit && !!token,
    queryFn: () => api.get(`/org/units/${selectedUnit}/members`) as Promise<Member[]>
  });

  const employeesQ = useQuery({
    queryKey:['employees-directory', !!token],
    enabled: !!isAdmin && !!token,
    queryFn: () => api.get('/employees') as Promise<DirectoryEmployee[]>
  });

  const uniqueEmployees = useMemo(() => {
    if (!employeesQ.data) return [];
    const seen = new Set<string>();
    return employeesQ.data
      .filter(emp => {
        const upnKey = emp.Upn?.trim().toLowerCase();
        if (!upnKey || !upnKey.includes('@')) return false;
        if (seen.has(upnKey)) return false;
        seen.add(upnKey);
        return true;
      })
      .sort((a, b) => {
        const aName = (a.DisplayName || a.Upn).toLowerCase();
        const bName = (b.DisplayName || b.Upn).toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [employeesQ.data]);

  const filteredEmployees = useMemo(() => {
    if (!uniqueEmployees.length) return [];
    const term = employeeFilter.trim().toLowerCase();
    if (!term) return uniqueEmployees;
    return uniqueEmployees.filter(e =>
      e.DisplayName?.toLowerCase().includes(term) || e.Upn.toLowerCase().includes(term)
    );
  }, [uniqueEmployees, employeeFilter]);

  async function createUnit(e:React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const result = await api.post('/org/units', { name: newName.trim() });
      setNewName('');
      qc.invalidateQueries({ queryKey:['org-units'] });
      showToast(`Team "${result.name}" ready \u2713`);
      setSelectedUnit(result.orgUnitId);
    } catch (err: any) {
      showToast(`Create failed: ${err?.message || 'Unknown error'}`);
    }
  }

  async function doRename(e:React.FormEvent) {
    e.preventDefault();
    if (!selectedUnit || !rename.trim()) return;
    try {
      await api.patch(`/org/units/${selectedUnit}`, { name: rename.trim() });
      setRename('');
      qc.invalidateQueries({ queryKey:['org-units'] });
      showToast('Team renamed \u2713');
    } catch (err: any) {
      showToast(`Rename failed: ${err?.message || 'Unknown error'}`);
    }
  }

  async function deleteUnit(id:number) {
    if (!confirm('Delete unit?')) return;
    try {
      await api.del(`/org/units/${id}`);
      if (selectedUnit === id) setSelectedUnit(undefined);
      qc.invalidateQueries({ queryKey:['org-units'] });
      showToast('Team deleted \u2713');
    } catch (err: any) {
      showToast(`Delete failed: ${err?.message || 'Unknown error'}`);
    }
  }

  async function addMember(e:React.FormEvent) {
    e.preventDefault();
    if (!selectedUnit || !addUpn.trim()) return;
    try {
      await api.post(`/org/units/${selectedUnit}/members`, { upn: addUpn.trim(), role: addRole });
      const name = filteredEmployees.find(emp => emp.Upn === addUpn)?.DisplayName || addUpn;
      showToast(`${name} added as ${addRole} \u2713`);
      setAddUpn('');
      setEmployeeFilter('');
      membersQ.refetch();
    } catch (err: any) {
      showToast(`Add member failed: ${err?.message || 'Unknown error'}`);
    }
  }

  async function removeMember(upn:string) {
    if (!selectedUnit) return;
    if (!confirm('Remove member?')) return;
    try {
      await api.del(`/org/units/${selectedUnit}/members?upn=${encodeURIComponent(upn)}`);
      membersQ.refetch();
      showToast('Member removed \u2713');
    } catch (err: any) {
      showToast(`Remove failed: ${err?.message || 'Unknown error'}`);
    }
  }

  return (
    <div style={{padding:'1rem'}}>
      <h2>Admin</h2>
      <div style={{display:'flex', gap:'2rem', alignItems:'flex-start', flexWrap:'wrap'}}>
        <section style={{flex:'1 1 280px'}}>
          <h3>Org Units</h3>
          {unitsQ.isLoading && <p>Loading units...</p>}
          {unitsQ.error && <p style={{color:'red'}}>Error loading units</p>}
          <ul style={{listStyle:'none', padding:0, margin:0}}>
            {unitsQ.data?.map(u => (
              <li key={u.orgUnitId} style={{display:'flex', justifyContent:'space-between', padding:'4px 0'}}>
                <button style={{flex:1, textAlign:'left', background:'none', border:'none', cursor:'pointer', fontWeight: selectedUnit===u.orgUnitId?700:400, color: '#0e2c32'}} onClick={()=>setSelectedUnit(u.orgUnitId)}>{u.name}</button>
                <button onClick={()=>deleteUnit(u.orgUnitId)} style={{marginLeft:8}}>🗑️</button>
              </li>
            ))}
          </ul>
          <form onSubmit={createUnit} style={{marginTop:'0.5rem'}}>
            <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="New unit name" />
            <button type="submit">Add</button>
          </form>
          {selectedUnit && (
            <form onSubmit={doRename} style={{marginTop:'0.5rem'}}>
              <input value={rename} onChange={e=>setRename(e.target.value)} placeholder="Rename selected" />
              <button type="submit">Rename</button>
            </form>
          )}
        </section>

        <section style={{flex:'2 1 420px'}}>
          <h3>Members {selectedUnit && `of #${selectedUnit}`}</h3>
          {!selectedUnit && <p>Select a unit</p>}
          {selectedUnit && membersQ.isLoading && <p>Loading members...</p>}
          {membersQ.error && <p style={{color:'red'}}>Error</p>}
          {selectedUnit && !membersQ.isLoading && (
            <table style={{minWidth:360}}>
              <thead><tr><th>UPN</th><th>Name</th><th>Role</th><th></th></tr></thead>
              <tbody>
                {membersQ.data?.map(m => (
                  <tr key={m.upn}>
                    <td>{m.upn}</td><td>{m.displayName}</td><td>{m.role}</td>
                    <td><button onClick={()=>removeMember(m.upn)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selectedUnit && (
            <form onSubmit={addMember} style={{marginTop:'0.5rem', display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center'}}>
              <div style={{display:'flex', flexDirection:'column'}}>
                <input
                  value={employeeFilter}
                  onChange={e=>setEmployeeFilter(e.target.value)}
                  placeholder="Filter users"
                  disabled={employeesQ.isLoading}
                />
              </div>
              <div style={{display:'flex', flexDirection:'column'}}>
                <select
                  value={addUpn}
                  onChange={e=>setAddUpn(e.target.value)}
                  style={{minWidth:220}}
                  disabled={employeesQ.isLoading}
                >
                  <option value="">Select user</option>
                  {filteredEmployees.map(emp => (
                    <option key={emp.EmployeeId} value={emp.Upn}>
                      {emp.DisplayName ? `${emp.DisplayName} (${emp.Upn})` : emp.Upn}
                    </option>
                  ))}
                </select>
              </div>
              <select value={addRole} onChange={e=>setAddRole(e.target.value as any)}>
                <option value="member">member</option>
                <option value="manager">manager</option>
              </select>
              <button type="submit" disabled={!addUpn.trim()}>Add Member</button>
              <button type="button" onClick={()=>employeesQ.refetch()}>Rescan</button>
              {employeesQ.isFetching && <span style={{fontSize:'0.85rem'}}>Refreshing…</span>}
            </form>
          )}
          {selectedUnit && employeesQ.error && <p style={{color:'red'}}>Failed to load employees</p>}
        </section>
      </div>
    </div>
  );
};

export default AdminPage;
